import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Loader from '@/components/ui/Loader';
import {
  buildDowngradeSummary,
  formatDurationLimit,
  type DowngradeTargetLimit,
} from '@/lib/downgradeImpacts';
import type { DowngradeImpactPreviewDto } from '@/features/billing/types';

type DowngradeImpactSummaryProps = {
  preview: DowngradeImpactPreviewDto | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
};

export default function DowngradeImpactSummary({
  preview,
  loading,
  error,
  onRetry,
}: Readonly<DowngradeImpactSummaryProps>) {
  const { t } = useTranslation('billing');
  const summary = buildDowngradeSummary(preview);

  const limitLabel = (limit: DowngradeTargetLimit): string => {
    if (limit.key === 'youtube') {
      return limit.allowed
        ? t('changePlan.downgrade.limit.youtubeKept')
        : t('changePlan.downgrade.limit.youtube');
    }
    if (limit.key === 'sessionDuration') {
      const { hours, minutes } = formatDurationLimit(limit.value);
      return minutes === 0
        ? t('changePlan.downgrade.limit.sessionDurationHours', { hours })
        : t('changePlan.downgrade.limit.sessionDurationHoursMinutes', {
            hours,
            minutes: String(minutes).padStart(2, '0'),
          });
    }
    if (limit.key === 'participants') {
      return t('changePlan.downgrade.limit.participants', { max: limit.value });
    }
    return t(`changePlan.downgrade.limit.${limit.key}`, { count: limit.value });
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-secondary text-sm">
        <Loader />
        {t('changePlan.downgrade.impactsLoading')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-[20px] border border-destructive/30 p-4 flex flex-col gap-3">
        <p className="text-sm text-destructive flex items-start gap-2">
          <AlertTriangle className="size-4 shrink-0 mt-0.5" />
          {t('changePlan.downgrade.impactsError')}
        </p>
        <Button type="button" variant="outline" className="self-start" onClick={onRetry}>
          {t('changePlan.downgrade.impactsRetry')}
        </Button>
      </div>
    );
  }

  if (!preview) return null;

  return (
    <div className="flex flex-col gap-4">
      {summary.hasImpacts ? (
        <div className="rounded-[20px] bg-[#1F1F1F]/3 p-4 flex flex-col gap-2">
          <p className="text-base font-medium flex items-start gap-2">
            <AlertTriangle className="size-4 shrink-0 mt-1" />
            {t('changePlan.downgrade.impactsTitle')}
          </p>
          <ul className="list-disc pl-9 text-sm text-secondary flex flex-col gap-1">
            {summary.impacts.map((impact) => (
              <li key={impact.key}>
                {t(`changePlan.downgrade.impact.${impact.key}`, { count: impact.count })}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-sm text-secondary flex items-start gap-2">
          <Check className="size-4 shrink-0 mt-0.5" />
          {t('changePlan.downgrade.noImpact')}
        </p>
      )}

      <div className="flex flex-col gap-2">
        <p className="text-base font-medium">{t('changePlan.downgrade.limitsTitle')}</p>
        <ul className="list-disc pl-5 text-sm text-secondary flex flex-col gap-1">
          {summary.limits.map((limit) => (
            <li key={limit.key}>{limitLabel(limit)}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
